import { Button } from "@designs-by-maryam/ui/components/button";
import { Skeleton } from "@designs-by-maryam/ui/components/skeleton";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { Loader2 } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { type Project, ProjectCard } from "@/components/projects/project-card";

type ProjectsGridProps = {
	projects: Project[];
	isLoading?: boolean;
	isLoadingMore?: boolean;
	hasMore?: boolean;
	onLoadMore?: () => void;
	onProjectClick?: (projectId: string) => void;
	className?: string;
	emptyMessage?: string;
};

/**
 * Loading skeleton for the projects grid with staggered animation
 */
function ProjectsGridSkeleton({ count = 12 }: { count?: number }) {
	const shouldReduce = useReducedMotion() ?? false;

	return (
		<div
			className={cn("grid gap-6", "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3")}
			data-testid="projects-grid-skeleton"
		>
			{Array.from({ length: count }, (_, i) => i).map((index) => (
				<motion.div
					animate={{ opacity: 1, y: 0 }}
					className="space-y-4"
					initial={shouldReduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
					key={`skeleton-${index}`}
					transition={{
						duration: 0.4,
						delay: shouldReduce ? 0 : index * 0.05,
						ease: "easeOut",
					}}
				>
					<div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
						<Skeleton className="h-full w-full animate-pulse bg-gradient-to-br from-muted via-muted/80 to-muted" />
					</div>
					<div className="space-y-2 p-4">
						<Skeleton className="h-5 w-3/4 animate-pulse" />
						<Skeleton className="h-4 w-1/2 animate-pulse delay-75" />
					</div>
				</motion.div>
			))}
		</div>
	);
}

/**
 * Empty state component for when no projects match the filter with fade-in animation
 */
function EmptyState({ message }: { message: string }) {
	const shouldReduce = useReducedMotion() ?? false;

	return (
		<motion.div
			animate={{ opacity: 1, scale: 1 }}
			aria-live="polite"
			className="flex flex-col items-center justify-center py-16 text-center"
			data-testid="projects-grid-empty"
			initial={
				shouldReduce ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }
			}
			transition={{ duration: 0.4, ease: "easeOut" }}
		>
			<motion.div
				animate={{ rotate: 0 }}
				className="mb-4 rounded-full bg-muted p-4"
				initial={shouldReduce ? { rotate: 0 } : { rotate: -10 }}
				transition={{ duration: 0.6, ease: "easeOut" }}
			>
				<svg
					aria-hidden="true"
					className="h-8 w-8 text-muted-foreground"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.5}
					/>
				</svg>
			</motion.div>
			<p className="text-lg text-muted-foreground">{message}</p>
		</motion.div>
	);
}

/**
 * ProjectsGrid Component
 *
 * Displays a responsive masonry-style grid of project cards with pagination.
 * Used on the portfolio page for the full project listing.
 *
 * Features:
 * - Responsive grid: 3 columns desktop, 2 columns tablet, 1 column mobile
 * - Staggered fade-in animations for cards
 * - Loading states with animated skeleton UI
 * - Empty state when no projects match filter
 * - Load More pagination button with loading spinner and hover effects
 * - Accessible with proper ARIA attributes
 */
export function ProjectsGrid({
	projects,
	isLoading = false,
	isLoadingMore = false,
	hasMore = false,
	onLoadMore,
	onProjectClick,
	className,
	emptyMessage = "No projects found.",
}: ProjectsGridProps) {
	const shouldReduce = useReducedMotion() ?? false;

	// Initial loading state
	if (isLoading) {
		return <ProjectsGridSkeleton />;
	}

	// Empty state
	if (projects.length === 0) {
		return <EmptyState message={emptyMessage} />;
	}

	return (
		<div className={className}>
			{/* Projects Grid with staggered animation */}
			<ul
				aria-label="Projects list"
				className={cn(
					"grid list-none gap-6",
					"grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
				)}
				data-testid="projects-grid"
			>
				{projects.map((project, index) => (
					<motion.li
						animate={{ opacity: 1, y: 0 }}
						initial={
							shouldReduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
						}
						key={project.id}
						transition={{
							duration: 0.4,
							delay: shouldReduce ? 0 : index * 0.05,
							ease: "easeOut",
						}}
					>
						<ProjectCard
							onClick={() => onProjectClick?.(project.id)}
							project={project}
						/>
					</motion.li>
				))}
			</ul>

			{/* Load More Button with hover effects */}
			{hasMore && onLoadMore && (
				<motion.div
					animate={{ opacity: 1, y: 0 }}
					className="mt-10 flex justify-center"
					initial={shouldReduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
					transition={{ duration: 0.4, delay: 0.2 }}
				>
					<Button
						aria-label={
							isLoadingMore ? "Loading more projects" : "Load more projects"
						}
						className={cn(
							"min-w-[200px] bg-primary text-primary-foreground transition-all duration-300 hover:bg-primary/90",
							"hover:scale-105 hover:shadow-lg hover:shadow-primary/20",
							"disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100",
						)}
						data-testid="load-more-button"
						disabled={isLoadingMore}
						onClick={onLoadMore}
						size="lg"
					>
						{isLoadingMore ? (
							<>
								<Loader2
									aria-hidden="true"
									className="mr-2 h-4 w-4 animate-spin"
								/>
								Loading...
							</>
						) : (
							"Load More Projects"
						)}
					</Button>
				</motion.div>
			)}

			{/* Screen reader announcement for loaded count */}
			<div aria-atomic="true" aria-live="polite" className="sr-only">
				{projects.length} projects displayed
			</div>
		</div>
	);
}

export default ProjectsGrid;
